import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import MediaImg from "@/components/MediaImg";
import { ikTransform } from "@/lib/media";

function inWindow(s) {
  const today = new Date().toISOString().slice(0, 10);
  if (s.start_date && today < s.start_date) return false;
  if (s.end_date && today > s.end_date) return false;
  return true;
}

export default function HeroSlider({ data }) {
  const [section, setSection] = useState(data);
  const [idx, setIdx] = useState(0);
  const [under, setUnder] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const [restReady, setRestReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setRestReady(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (data !== undefined) { setSection(data); return; }
    api.get("/website/sections/hero_slider").then((r) => setSection(r.data)).catch(() => setSection(null));
  }, [data]);

  const settings = section?.settings || {};
  const slides = (section?.items || []).filter((s) => s.visible !== false && s.image && inWindow(s));
  const intervalMs = Math.max(2, Number(settings.interval) || 5) * 1000;
  const fade = settings.transition !== "slide";

  const go = useCallback((n) => {
    setIdx((i) => (slides.length ? ((n % slides.length) + slides.length) % slides.length : 0));
  }, [slides.length]);

  useEffect(() => {
    if (!settings.autoplay || paused || slides.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(t);
  }, [settings.autoplay, paused, slides.length, intervalMs]);

  useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides.length, idx]);

  useEffect(() => {
    if (idx === under) return undefined;
    const t = setTimeout(() => setUnder(idx), 720);
    return () => clearTimeout(t);
  }, [idx, under]);

  useEffect(() => { if (under >= slides.length) setUnder(0); }, [slides.length, under]);

  if (section === undefined) {
    return (
      <section className="relative overflow-hidden bg-[#0F1115]" data-testid="hero-slider-loading" aria-hidden="true">
        <div className="w-full aspect-[4/5] sm:aspect-[8/3]" />
      </section>
    );
  }
  if (section === null || section.active === false || slides.length === 0) return null;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  const slideInner = (s, i) => {
    if (i > 0 && !restReady) return null;
    return (
    <>
      <picture className="absolute inset-0">
        {s.mobile_image && <source media="(max-width: 640px)" srcSet={ikTransform(s.mobile_image, 800)} />}
        <MediaImg src={s.image} alt={s.name || `Banner ${i + 1}`} className="w-full h-full object-cover"
          eager={i === 0} sizes="100vw" width={1600} height={600} testid={`hero-slide-img-${i}`} />
      </picture>
      {s.cta_label && s.cta_link && (
        <Link to={s.cta_link} data-testid={`hero-cta-${i}`}
          className="absolute left-5 bottom-6 sm:left-10 sm:bottom-10 inline-flex items-center gap-2 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-extrabold px-6 py-3 sm:px-7 sm:py-3.5 text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-lg">
          {s.cta_label} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </>
    );
  };

  return (
    <section className="relative overflow-hidden bg-[#0F1115] select-none" data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="relative w-full aspect-[4/5] sm:aspect-[8/3]">
        {fade ? (
          slides.map((s, i) => (
            <div key={i} className={`absolute inset-0 ${i === idx ? "opacity-100 z-20 transition-opacity duration-700" : i === under ? "opacity-100 z-10 pointer-events-none" : "opacity-0 z-0 pointer-events-none"}`}
              data-testid={`hero-slide-${i}`} aria-hidden={i !== idx}>
              {slideInner(s, i)}
            </div>
          ))
        ) : (
          <div className="absolute inset-0 flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${idx * 100}%)` }} data-testid="hero-slide-track">
            {slides.map((s, i) => (
              <div key={i} className="relative w-full h-full shrink-0" data-testid={`hero-slide-${i}`}>{slideInner(s, i)}</div>
            ))}
          </div>
        )}
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={() => go(idx - 1)} aria-label="Previous banner" data-testid="hero-prev"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-[#FFC72C] text-[#17263B] flex items-center justify-center shadow-lg transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => go(idx + 1)} aria-label="Next banner" data-testid="hero-next"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-[#FFC72C] text-[#17263B] flex items-center justify-center shadow-lg transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button key={i} onClick={() => go(i)} aria-label={`Go to banner ${i + 1}`} data-testid={`hero-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-[#FFC72C]" : "w-2 bg-white/60 hover:bg-white"}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
